import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Users,
  Layout,
  Code,
  Shield,
  Globe,
  PieChart,
} from "lucide-react";

const ProcessSection = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
    });
  }, []);

const whatWeOffer = [
  {
    icon: Globe,
    title: "24/7 System Monitoring",
    description:
      "Real-time monitoring of servers, applications, and databases to detect and address issues before they affect users.",
  },
  {
    icon: Layout,
    title: "Bug Fixing & Issue Resolution",
    description:
      "Rapid identification and resolution of technical bugs, errors, and system inconsistencies to keep everything running smoothly.",
  },
  {
    icon: Users,
    title: "Regular Updates & Patch Management",
    description:
      "Routine updates, security patches, and version upgrades to ensure your systems remain secure, stable, and up to date.",
  },
  {
    icon: PieChart,
    title: "Performance & Speed Optimization",
    description:
      "Continuous performance tuning to maintain fast, scalable, and responsive application behavior at all times.",
  },
  {
    icon: Code,
    title: "Backup & Disaster Recovery",
    description:
      "Automated backups, strategic recovery planning, and quick restoration to protect your data from unexpected failures.",
  },
  {
    icon: Shield,
    title: "Security Monitoring & Threat Prevention",
    description:
      "Ongoing security surveillance, vulnerability scanning, and proactive threat prevention to safeguard your digital ecosystem.",
  },
];


const processSteps = [
  {
    number: "01",
    title: "Assessment & Support Setup",
    description:
      "We assess your system architecture, define support priorities, configure SLAs, and set up monitoring tools for continuous oversight.",
    icon: Users,
      imagePath: "./images/servicesImages/Assessment & Support Setup.png",
  },
  {
    number: "02",
    title: "Monitoring & Issue Detection",
    description:
      "Advanced monitoring tools track uptime, performance metrics, and security risks, helping us detect issues instantly.",
    icon: Layout,
      imagePath: "./images/servicesImages/Monitoring & Issue Detection.png",
  },
  {
    number: "03",
    title: "Resolution, Optimization & Updates",
    description:
      "Technical issues are resolved quickly, performance is optimized, and system updates are applied to maintain long-term stability.",
    icon: Code,
      imagePath: "./images/servicesImages/Resolution, Optimization & Updates.png",
  },
  {
    number: "04",
    title: "Reporting, Review & Continuous Improvement",
    description:
      "We deliver detailed reports, analyze recurring issues, and implement improvements to enhance overall system reliability.",
    icon: Shield,
      imagePath: "./images/servicesImages/Reporting, Review & Continuous Improvement.png",
  },
];


const ourEdge = [
  "⚡ Proactive Support: Predictive monitoring and smart alerts help prevent issues before they disrupt operations.",
  "🔐 Security First Approach: Continuous threat detection, patching, and vulnerability management ensure maximum protection.",
  "🚀 Performance Guaranteed: Ongoing optimization keeps your system consistently fast, reliable, and scalable.",
  "📊 Transparent Reporting: You receive clear health reports, analytics, and actionable suggestions for improvement.",
];


const howWeWork = [
  "1. System Analysis & SLA Planning – We understand your application’s structure and define a tailored support plan.",
  "2. Monitoring & Preventive Care – We continuously monitor system health, performance, and security parameters.",
  "3. Fast Issue Resolution – Our experts address bugs, outages, and bottlenecks quickly to restore smooth operation.",
  "4. Continuous Updates & Optimization – Regular maintenance, updates, and performance tuning ensure long-term stability.",
];


  return (
    <section className="py-20 px-[5%] bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        {/* What We Offer Section */}
        <div className="mb-28">
          <h2
            className="text-center text-3xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-12"
            data-aos="fade-up"
          >
            What We Offer
          </h2>
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {whatWeOffer.map((feature, index) => (
              <div
                key={index}
                className="bg-[#dcfaf9] rounded-full p-8 shadow-md border-t-4 border-b-4 border-[#0d6b68] hover:shadow-xl transition-all duration-300"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="flex items-center gap-4 mb-4">
                  <feature.icon className="w-8 h-8 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-32" data-aos="fade-up">
  <h2 className="text-4xl md:text-6xl font-semibold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-6">
    Our Process
  </h2>

  <p className="text-gray-600 text-lg sm:text-xl max-w-2xl mx-auto">
    We follow a structured and proactive support process to keep your systems
    stable, secure, and high-performing. From continuous monitoring to fast
    issue resolution and regular updates, every step ensures long-term
    reliability and smooth operations.
  </p>
</div>


        {/* Process Grid */}
        <div className="grid md:grid-cols-2 gap-y-32 gap-x-10">
          {processSteps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <div
                key={index}
                className="flex flex-col relative"
                data-aos="fade-up"
                data-aos-delay={index * 200}
              >
                {/* Background Number */}
                <div className="absolute -top-24 text-6xl md:text-9xl font-bold text-[#9ee8e7] select-none pointer-events-none z-0">
                  {step.number}
                </div>

                {/* Card */}
                <div className="bg-gradient-to-br from-[#0d6b68] to-[#baf7f6] rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 z-10 border border-gray-100">
                  {/* Card Image/Icon Area */}
                  <div className="mb-8 relative">
                    <div className="w-full h-fit bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl flex items-center justify-center overflow-hidden relative">
                      <img
                        src={step.imagePath}
                        alt={step.title}
                        className="w-full h-38 md:h-40 lg:h-52 object-cover rounded-2xl"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextElementSibling.style.display = "flex";
                        }}
                      />

                      {/* Fallback Icon */}
                      <div className="absolute inset-0 hidden items-center justify-center">
                        <div className="relative">
                          <div className="absolute -top-4 -left-4 w-3 h-3 bg-blue-400 rounded-full opacity-60"></div>
                          <div className="absolute -bottom-6 -right-6 w-2 h-2 bg-purple-400 rounded-full opacity-40"></div>
                          <div className="absolute top-2 right-4 w-1.5 h-1.5 bg-indigo-400 rounded-full opacity-50"></div>
                          <div className="bg-white p-6 rounded-2xl shadow-lg">
                            <IconComponent className="w-12 h-12 text-blue-600" />
                          </div>
                          <div className="absolute top-1/2 -left-8 w-6 h-0.5 bg-blue-300 opacity-60"></div>
                          <div className="absolute top-1/2 -right-8 w-6 h-0.5 bg-blue-300 opacity-60"></div>
                          <div className="absolute -top-8 left-1/2 w-0.5 h-6 bg-blue-300 opacity-60"></div>
                          <div className="absolute -bottom-8 left-1/2 w-0.5 h-6 bg-blue-300 opacity-60"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Our Edge Section */}
        <div className="text-center mt-32" data-aos="fade-up">
          <h2
            className="text-4xl md:text-6xl font-semibold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-8"
            style={{ lineHeight: "1.4" }}
          >
            Our Edge
          </h2>

          <ul className="text-gray-700 max-w-4xl mx-auto space-y-4 text-lg">
            {ourEdge.map((point, index) => (
              <li key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                {point}
              </li>
            ))}
          </ul>
        </div>

        {/* How We Work Section */}
        <div className="text-center mt-20" data-aos="fade-up">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-8 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            How We Work
          </h2>
          <ul className="text-gray-700 max-w-3xl mx-auto space-y-4 text-lg">
            {howWeWork.map((step, index) => (
              <li key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                {step}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
